import React, { useState, useRef, useEffect } from 'react'
import { HeaderProps, NavItemConfig, NavItemGroup } from './Header.types'
import {
  StyledHeader,
  LogoSection,
  NavSection,
  NavItem,
  NavItemWrapper,
  SubmenuContainer,
  SubmenuItem,
  SubmenuArrow,
  SearchSection,
  MobileMenuButton,
  DesktopOnly,
  UserMenu,
  IconButton,
  UserAvatar,
  UserName,
  Avatar,
  MobileMenu,
  MobileNavSection,
  MobileNavItem,
  MobileSubmenu,
  MobileSubmenuItem,
} from './Header.styles'
import { Text } from '../../atoms/Text'
import { Icon } from '../../atoms/Icon'
import { Input } from '../../atoms/Input'

function isNavItemGroup(item: NavItemConfig): item is NavItemGroup {
  return 'items' in item
}

export const Header: React.FC<HeaderProps> = ({
  logo,
  title,
  showSearch = false,
  searchPlaceholder = 'Search...',
  onSearchChange,
  navItems = [],
  actions,
  user,
  onUserClick,
  variant = 'default',
  sticky = false,
  className,
}) => {
  const [searchValue, setSearchValue] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)
  const [_mobileOpenSubmenu, _setMobileOpenSubmenu] = useState<string | null>(
    null
  )
  const [mobileExpandedSubmenus, setMobileExpandedSubmenus] = useState<
    Set<string>
  >(new Set())
  const submenuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchValue(value)
    onSearchChange?.(value)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openSubmenu) {
        const submenuRef = submenuRefs.current[openSubmenu]
        if (submenuRef && !submenuRef.contains(event.target as Node)) {
          setOpenSubmenu(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openSubmenu])

  useEffect(() => {
    // Close mobile menu when resizing to desktop
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    // Initialize expanded submenus when mobile menu opens
    if (mobileMenuOpen && mobileExpandedSubmenus.size === 0) {
      const expandedSet = new Set<string>()
      navItems.forEach((item) => {
        if (isNavItemGroup(item)) {
          expandedSet.add(item.label)
        }
      })
      setMobileExpandedSubmenus(expandedSet)
    }
  }, [mobileMenuOpen, navItems, mobileExpandedSubmenus.size])

  const handleNavClick = (item: NavItemConfig) => {
    if (isNavItemGroup(item)) {
      setOpenSubmenu(openSubmenu === item.label ? null : item.label)
    } else if (item.onClick) {
      item.onClick()
      setOpenSubmenu(null)
    }
  }

  const handleMobileNavClick = (item: NavItemConfig) => {
    if (isNavItemGroup(item)) {
      const newExpanded = new Set(mobileExpandedSubmenus)
      if (newExpanded.has(item.label)) {
        newExpanded.delete(item.label)
      } else {
        newExpanded.add(item.label)
      }
      setMobileExpandedSubmenus(newExpanded)
    } else if (item.onClick) {
      item.onClick()
      setMobileMenuOpen(false)
    }
  }

  const handleMobileSubmenuClick = (subItem: any) => {
    subItem.onClick?.()
    setMobileMenuOpen(false)
  }

  return (
    <StyledHeader $sticky={sticky} $variant={variant} className={className}>
      {variant !== 'minimal' && (
        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <Icon name={mobileMenuOpen ? 'close' : 'menu'} />
        </MobileMenuButton>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
        {(logo || title) && (
          <LogoSection>
            {logo}
            {title && (
              <Text variant="h5" weight="semibold">
                {title}
              </Text>
            )}
          </LogoSection>
        )}

        <DesktopOnly>
          {navItems.length > 0 && variant !== 'minimal' && (
            <NavSection $variant={variant} style={{ marginLeft: '130px' }}>
              {navItems.map((item, index) => {
                const isGroup = isNavItemGroup(item)
                const isOpen = isGroup && openSubmenu === item.label

                return (
                  <NavItemWrapper
                    key={index}
                    ref={(el) => {
                      if (isGroup && el) {
                        submenuRefs.current[item.label] = el
                      }
                    }}
                  >
                    <NavItem
                      $active={!!item.active}
                      onClick={() => handleNavClick(item)}
                    >
                      {item.label}
                      {isGroup && (
                        <SubmenuArrow
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          style={{
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                          }}
                        >
                          <path d="M6 9l6 6 6-6" />
                        </SubmenuArrow>
                      )}
                    </NavItem>
                    {isGroup && (
                      <SubmenuContainer $isOpen={isOpen}>
                        {item.items.map((subItem, subIndex) => (
                          <SubmenuItem
                            key={subIndex}
                            $active={subItem.active}
                            onClick={() => {
                              subItem.onClick?.()
                              setOpenSubmenu(null)
                            }}
                          >
                            {subItem.icon}
                            {subItem.label}
                          </SubmenuItem>
                        ))}
                      </SubmenuContainer>
                    )}
                  </NavItemWrapper>
                )
              })}
            </NavSection>
          )}

          {showSearch && (
            <SearchSection>
              <Input
                fullWidth
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={handleSearchChange}
                leftIcon={<Icon name="search" size="small" />}
              />
            </SearchSection>
          )}
        </DesktopOnly>
      </div>

      <UserMenu>
        <DesktopOnly>
          <IconButton title="Help">
            <svg className="icon" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </IconButton>
          {actions && actions}
        </DesktopOnly>

        {user && (
          <UserAvatar onClick={onUserClick}>
            <UserName>{user.name}</UserName>
            <Avatar>
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                />
              ) : (
                user.initials
              )}
            </Avatar>
          </UserAvatar>
        )}
      </UserMenu>

      {/* Mobile Menu */}
      <MobileMenu $isOpen={mobileMenuOpen}>
        <MobileNavSection>
          {navItems.map((item, index) => {
            const isGroup = isNavItemGroup(item)
            const isOpen = isGroup && mobileExpandedSubmenus.has(item.label)

            return (
              <div key={index}>
                <MobileNavItem
                  $active={!!item.active}
                  onClick={() => handleMobileNavClick(item)}
                >
                  {item.label}
                  {isGroup && (
                    <SubmenuArrow
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        width: '16px',
                        height: '16px',
                      }}
                    >
                      <path d="M6 9l6 6 6-6" />
                    </SubmenuArrow>
                  )}
                </MobileNavItem>
                {isGroup && (
                  <MobileSubmenu $isOpen={isOpen}>
                    {item.items.map((subItem, subIndex) => (
                      <MobileSubmenuItem
                        key={subIndex}
                        $active={subItem.active}
                        onClick={() => handleMobileSubmenuClick(subItem)}
                      >
                        {subItem.icon}
                        {subItem.label}
                      </MobileSubmenuItem>
                    ))}
                  </MobileSubmenu>
                )}
              </div>
            )
          })}
        </MobileNavSection>

        {showSearch && (
          <div style={{ marginTop: '16px' }}>
            <Input
              fullWidth
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={handleSearchChange}
              leftIcon={<Icon name="search" size="small" />}
            />
          </div>
        )}

        {actions && <div style={{ marginTop: '16px' }}>{actions}</div>}
      </MobileMenu>
    </StyledHeader>
  )
}
